import { useCallback } from 'react'
import { useSignIn } from 'utils/hooks/useSignIn'
import { useUserContext } from 'apps/fleet-planner/context'
import {
  authTokenLogin,
  setUserPassword,
  requestLoginLink,
  setAuthCookies,
  authTokenReturningUserLogin,
} from 'apps/fleet-planner/utils/api'

interface IUserInfo {
  firstName?: string | null
  lastName?: string | null
  phone?: string | null
  cellPhone?: string | null
}

interface ICreateNewUserArgs {
  /**
   * String token passed via URL param to browser
   * @example 'adjad9Hfh2BF9ahd'
   */
  confirmationToken: string
  password: string
  userInfo?: IUserInfo
}

interface IUseUserAuthProps {
  /**
   * Callback triggered on successful auth flow
   */
  onSuccess?: (data?: any) => void
}

/**
 * Handles logic around login for Fleet PLanner
 */
export const useUserAuth = ({ onSuccess: onSuccessProp = () => {} }: IUseUserAuthProps = {}) => {
  const { fetchUserData, updateUserInfo } = useUserContext()
  const { mutate: signInWithMagicToken } = useSignIn({})

  /**
   * Bundles logic for final step of onboarding
   */
  const createNewUser = async ({
    password,
    confirmationToken,
    userInfo: user = {},
  }: ICreateNewUserArgs) => {
    const { cellPhone = null, phone = null } = user || {}
    await setUserPassword({ confirmationToken, password, phone, cellPhone })

    const loginResponse = await authTokenLogin(confirmationToken)

    // Set auth headers and cookies
    await setAuthCookies(loginResponse)

    // PUT requests updates user information
    await updateUserInfo({ user })

    // When creating an account on mobile, pass the userDetails and session data back to the app
    const stringifiedUserDetails = JSON.stringify({
      ...loginResponse,
      ...user,
    })

    // trigger next step
    onSuccessProp(stringifiedUserDetails)
  }

  /**
   * Bundles logic for final step of onboarding
   */
  const handleReturningUser = useCallback(
    async (resetPasswordToken: string) => {
      try {
        const loginResponse = await authTokenReturningUserLogin(resetPasswordToken)
        // Set auth headers and cookies
        await setAuthCookies(loginResponse)
        // PUT requests updates user information
        await fetchUserData()
      } catch (e) {
        throw new Error(e)
      }
    },
    [fetchUserData]
  )

  /**
   * Logs user in via reset password token
   */
  const handleTokenLogin = useCallback(
    async (magicToken: string) => {
      try {
        const loginResponse = await signInWithMagicToken({
          userLogin: {
            magicToken,
          },
        })

        // Set auth headers and cookies
        await setAuthCookies(loginResponse)

        await fetchUserData()
        return true
      } catch (e) {
        return false
      }
    },
    [fetchUserData, signInWithMagicToken]
  )

  /**
   * Sends link via email to new or returning user
   */
  const requestLink = useCallback((email: string): Promise<void> => {
    return requestLoginLink(email)
  }, [])

  return {
    handleReturningUser,
    createNewUser,
    requestLink,
    handleTokenLogin,
  }
}
